import React, { useContext, useEffect, useRef, useState } from "react";
import "./GallerySection2.css";
import { MultiSelect } from "primereact/multiselect";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchGalleryData } from "../../redux/gallerySlice";
import Loader from "../Loader/loader";
import ImageWithObjectFit from "../ImageWrapperComponent";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Paginator } from "primereact/paginator";
import { WindowContext } from "../WindowResize/resize";
import { Button } from "../Button";
import { Dialog } from "primereact/dialog";
import parse from "html-react-parser";
import useCustomTranslation from "../../utils/useCustomTranslation";
import { getTargetFromType, useGetLink } from "../../utils/useOpenLink";
import * as posterImage from "../../utils/usePosterImage";
import { useGetDateToDisplay } from "../../utils/dateFunctions";
import useGetArticleMeta from "../../utils/useGetArticleMeta";
import { ZERO_WIDTH_CHAR } from "../../constants/displayConstants";
import PlayIconSvg from "../../assets/PlayIconSvg";
import { InputText } from "primereact/inputtext";
import { selectCountryLanguageCodes } from "../../redux/authSlice";
import { useGetPageParam } from "../../utils/useGetPageParam";

function GallerySection2({ heading, body, contents: contentsArray, colors: colorObj }) {
    const { pathname, search } = useLocation();

    const dispatch = useDispatch();

    const navigate = useNavigate();
    const { getLink } = useGetLink();

    const getDateToDisplay = useGetDateToDisplay();

    const { t } = useCustomTranslation();

    const { isMobile } = useContext(WindowContext);

    const { languageCode } = useSelector(selectCountryLanguageCodes, shallowEqual);

    const headingRef = useRef(null);

    const cardRef = useRef(null);

    const dynamicFilters = useSelector((state) => state.gallery.dynamicFilters);
    const showFilters = Object.keys(dynamicFilters).reduce(
        (acc, cur) => acc + dynamicFilters[cur].Items.length,
        0,
    );

    const isLoading = useSelector((state) => state.gallery.isLoading);

    const [totalPageCount, setTotalPageCount] = useState(0);

    const [articleDetails, setArticleDetails] = useState([]);

    const [searchKeyword, setSearchKeyword] = useState("");

    const content = useSelector((state) => state.gallery.contents);

    const [visible, setVisible] = useState(false);

    const [selectedFilters, setSelectedFilters] = useState({});

    const [prevParams, setPrevParams] = useState({});

    const [prevPage, setPrevPage] = useState(0);

    const [paramsReq, setParamsReq] = useState();

    const [pageParam, setPageParam, getProcessedRequestObject] = useGetPageParam();

    const { getTheme, getSection } = useGetArticleMeta();

    const [isFirstRender, setIsFirstRender] = useState(true);

    useEffect(() => {
        if (pageParam.page !== prevPage) {
            setPrevPage(paramsReq.page);
            setTimeout(() => {
                ScrollTop();
            }, 50);
        }
    }, [pageParam.page]);

    useEffect(() => {
        const queryParams = new URLSearchParams(search);
        const req = { ...paramsReq };
        const selectedFilters = {};

        queryParams.forEach((value, key) => {
            if (key.startsWith("search_") && !key.startsWith("search_text")) {
                const filterKey = key.slice(7);
                const filterValues = value ? value.split(",") : [];

                if (filterValues.length > 0) {
                    let saveItems = [];
                    filterValues.forEach((item) => {
                        if (item !== "") {
                            saveItems.push({ itemName: decodeURIComponent(item) });
                        }
                    });

                    selectedFilters[filterKey] = saveItems;
                    req[`search_${filterKey}`] = filterValues;
                }
            } else if (key === "search_text") {
                setSearchKeyword(value);
                req.search_text = value;
            } else {
                if (key !== "page") {
                    req[key] = value;
                }
            }
        });
        setSelectedFilters(selectedFilters);
        setParamsReq(req);
    }, []);

    useEffect(() => {
        if (isFirstRender) {
            setIsFirstRender(false);
            return;
        }

        const currentReqObj = getProcessedRequestObject(paramsReq);

        if (
            Object.keys(pageParam).length > 0 &&
            JSON.stringify(currentReqObj) !== JSON.stringify(prevParams)
        ) {
            setPrevParams(currentReqObj);

            if (contentsArray.length > 0) {
                const apiLink = contentsArray[0].contentKey.slice(0, -1);
                dispatch(fetchGalleryData({ reqObj: currentReqObj, apiLink }));
            }
        }
    }, [paramsReq, pageParam, contentsArray, dispatch]);

    useEffect(() => {
        const queryParams = new URLSearchParams(search);

        for (const key in paramsReq) {
            if (paramsReq[key] && key.startsWith("search_") && !key.startsWith("search_text")) {
                queryParams.set(key, paramsReq[key].map(encodeURIComponent).join(","));
            }
        }

        if (searchKeyword !== "") {
            queryParams.set("search_text", encodeURIComponent(searchKeyword));
        }

        if (pageParam.page > 0) {
            queryParams.set("page", pageParam.page + 1);
        }

        navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
    }, [paramsReq, searchKeyword, pageParam]);

    const fetchGalleryDetails = async () => {
        setArticleDetails([]);
        setTotalPageCount(0);

        if (content) {
            let articleArr = [];
            articleArr = articleArr.concat(content.articles);
            articleArr = JSON.parse(JSON.stringify(articleArr));
            for await (const element of articleArr) {
                if (element && element.link) {
                    element.posterImage = await posterImage.usePosterImage(element?.link);
                }
            }
            setArticleDetails(articleArr);
            setTotalPageCount(content.total);
        }
    };

    useEffect(() => {
        const getData = async () => {
            await fetchGalleryDetails();
        };
        getData();
    }, [content, isLoading]);

    const ScrollTop = () => {
        requestAnimationFrame(() => {
            if (headingRef.current) {
                const headingRect = headingRef.current.getBoundingClientRect();

                if (headingRect.top !== undefined) {
                    const offset = window.innerHeight / 5;
                    const top = headingRect.top + window.pageYOffset - offset;
                    window.scrollTo({ top, behavior: "smooth" });
                }
            } else if (cardRef.current) {
                const cardRect = cardRef.current.getBoundingClientRect();
                const offset = window.innerHeight / 5;

                if (cardRect.top !== undefined) {
                    const top = cardRect.top + window.pageYOffset - offset;
                    window.scrollTo({ top, behavior: "smooth" });
                }
            }
        });
    };

    const onPageChange = (event) => {
        setPageParam({ ...paramsReq, page_size: event.rows, page: event.page });
    };

    const onChangeSelectFilter = (value, filterName) => {
        let arr = [];
        let arrWithItemName = [];

        for (const iterator of value) {
            let str = encodeURIComponent(iterator.itemName);
            arr.push(str);
            arrWithItemName.push({ itemName: iterator.itemName });
        }

        const key = `search_${filterName.toLowerCase()}`;

        setParamsReq((prevState) => ({
            ...prevState,
            [key]: arr,
            page: 0,
        }));
        setSelectedFilters((prev) => ({
            ...prev,
            [filterName]: arrWithItemName,
        }));
    };

    const clearAllFilter = () => {
        setParamsReq({
            page: 0,
            page_size: 12,
        });
        setSelectedFilters({});
        navigate("/", { replace: true });

        setTimeout(() => {
            const cleanUrl = `/${pathname.slice(1)}`;
            navigate(cleanUrl, { replace: true });
        }, 0);
    };

    const clearSearch = () => {
        setParamsReq({
            ...paramsReq,
            search_text: "",
        });
        setSearchKeyword("");
    };

    const applyMobileFilters = () => {
        setVisible(false);
    };

    return (
        <>
            <Loader isLoading={isLoading} />
            {(heading || body) && (
                <section className="gallerySection2 padTopBottom headerBackground" ref={headingRef}>
                    <div className="container">
                        <div className="row">
                            <div className="col-12" data-aos="fade-up">
                                <div className="heading" style={{ color: colorObj?.accent_color1 }}>
                                    {heading}
                                </div>
                                <div className="body" style={{ color: colorObj?.accent_color1 }}>
                                    {body && parse(body)}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            <section className="gallerySection2 mb-50 pb-0" ref={cardRef}>
                <div className="container">
                    <div className="row">
                        {showFilters ? (
                            <div className="filtersHldr">
                                <div className="col-12">
                                    <div
                                        className="filters-main"
                                        style={{
                                            borderTop: `1px solid ${colorObj?.accent_color2}`,
                                            borderBottom: `1px solid ${colorObj?.accent_color2}`,
                                        }}
                                    >
                                        <div className="col-lg-12 col-md-12 col-sm-12 col-12 flex items-center">
                                            <div
                                                className="filter-label"
                                                style={{
                                                    color: colorObj?.accent_color3,
                                                }}
                                            >
                                                {t("btnText.filters:")}
                                            </div>

                                            {Object.keys(dynamicFilters).map(
                                                (filterKey) =>
                                                    dynamicFilters[filterKey].Items &&
                                                    dynamicFilters[filterKey].Items.length > 0 && (
                                                        <div
                                                            className="tag-dropdown-main"
                                                            key={filterKey}
                                                        >
                                                            <MultiSelect
                                                                placeholder={
                                                                    dynamicFilters[filterKey].Label
                                                                }
                                                                value={selectedFilters[filterKey]}
                                                                onChange={(e) =>
                                                                    onChangeSelectFilter(
                                                                        e.value,
                                                                        filterKey,
                                                                    )
                                                                }
                                                                options={
                                                                    dynamicFilters[filterKey]
                                                                        .Items || []
                                                                }
                                                                optionLabel="itemName"
                                                                selectedItemsLabel={`${selectedFilters[
                                                                    filterKey
                                                                ]
                                                                    ?.slice(0, 2)
                                                                    ?.map((i) => i.itemName)
                                                                    ?.join(", ")}${
                                                                    selectedFilters[filterKey]
                                                                        ?.length > 2
                                                                        ? " ..."
                                                                        : ""
                                                                }`}
                                                                maxSelectedLabels={0}
                                                                filter
                                                                style={{
                                                                    "--accent_color1":
                                                                        colorObj?.accent_color1,
                                                                }}
                                                            />
                                                        </div>
                                                    ),
                                            )}

                                            {Object.keys(selectedFilters).length > 0 && (
                                                <div
                                                    className="clear-all-txt"
                                                    style={{ color: colorObj?.primary_color }}
                                                    onClick={() => clearAllFilter()}
                                                >
                                                    {t("btnText.clearAll")}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-lg-3 col-md-12 col-sm-12 col-12">
                                            <div className="search-hldr">
                                                <i className="pi pi-search"></i>
                                                {searchKeyword !== "" && (
                                                    <span
                                                        className="cursor-pointer"
                                                        onClick={clearSearch}
                                                    >
                                                        <i className="search1 pi pi-times"></i>
                                                    </span>
                                                )}
                                                <InputText
                                                    type="text"
                                                    placeholder={t("placeHolder.search")}
                                                    value={searchKeyword}
                                                    onChange={(e) => {
                                                        setSearchKeyword(e.target.value);
                                                    }}
                                                    className="ml-2"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {showFilters ? (
                            <div className="mobileFiltersHldr">
                                <div className="col-12">
                                    <div className="search-hldr">
                                        <i className="pi pi-search"></i>
                                        {searchKeyword !== "" && (
                                            <span className="cursor-pointer" onClick={clearSearch}>
                                                <i className="search1 pi pi-times"></i>
                                            </span>
                                        )}
                                        <InputText
                                            type="text"
                                            placeholder={t("placeHolder.search")}
                                            value={searchKeyword}
                                            onChange={(e) => {
                                                setSearchKeyword(e.target.value);
                                            }}
                                            className="ml-2"
                                        />
                                    </div>
                                </div>
                                <div className="col-12 mt-4">
                                    <div className="filter-btn" onClick={() => setVisible(true)}>
                                        <Button className="button">
                                            <i className="pi pi-sliders-h mr-2"></i>
                                            {t("btnText.filters")}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        <div className="col-12 mb-6">
                            <div className="row">
                                {articleDetails.length > 0 && (
                                    <>
                                        {articleDetails.map((value, index) => {
                                            const theme = getTheme(value?.breadcrumb);
                                            const section = getSection(value?.breadcrumb);

                                            return (
                                                <div
                                                    className="col-md-4 mb-6"
                                                    key={value?.pk || value?.title || index}
                                                >
                                                    {value && (
                                                        <div
                                                            className="newsCard card"
                                                            data-aos="fade-up"
                                                        >
                                                            <div className="cardImg">
                                                                {!!value?.link && (
                                                                    <div
                                                                        className="playIconHldr"
                                                                        style={{
                                                                            "--accent_color4":
                                                                                colorObj.accent_color4,
                                                                        }}
                                                                    >
                                                                        <PlayIconSvg />
                                                                    </div>
                                                                )}
                                                                <Link
                                                                    to={getLink(
                                                                        value.path,
                                                                        value.type,
                                                                        value.language,
                                                                        value.link,
                                                                    )}
                                                                    target={getTargetFromType(
                                                                        value.type,
                                                                        value.link,
                                                                    )}
                                                                    className="cursor-pointer"
                                                                >
                                                                    <ImageWithObjectFit
                                                                        className="cursor-pointer"
                                                                        src={
                                                                            value?.image?.src ||
                                                                            value?.posterImage
                                                                        }
                                                                    />
                                                                </Link>
                                                            </div>
                                                            <div className="card-body flex flex-col text-start">
                                                                <div
                                                                    className="tag-heading"
                                                                    style={{
                                                                        color: colorObj.primary_color,
                                                                    }}
                                                                >
                                                                    {theme || ZERO_WIDTH_CHAR}
                                                                </div>
                                                                <Link
                                                                    to={getLink(
                                                                        value.path,
                                                                        value.type,
                                                                        value.language,
                                                                        value.link,
                                                                    )}
                                                                    target={getTargetFromType(
                                                                        value.type,
                                                                        value.link,
                                                                    )}
                                                                    className="hover:underline cursor-pointer title"
                                                                    style={{
                                                                        color: colorObj?.accent_color1,
                                                                    }}
                                                                >
                                                                    {value && value.title
                                                                        ? value.title
                                                                        : ""}
                                                                </Link>

                                                                <div className="authorDateMain mt-auto">
                                                                    <span
                                                                        className="author ltr:mr-1 rtl:ml-1 capitalize"
                                                                        style={{
                                                                            "--accent_color1":
                                                                                colorObj.accent_color1,
                                                                        }}
                                                                    >
                                                                        {section}
                                                                    </span>
                                                                    <span
                                                                        className="date"
                                                                        style={{
                                                                            "--accent_color3":
                                                                                colorObj.accent_color3,
                                                                        }}
                                                                    >
                                                                        {section && value.date
                                                                            ? " • "
                                                                            : ""}
                                                                        {getDateToDisplay(
                                                                            value.date,
                                                                            languageCode,
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </div>
                        </div>
                        {articleDetails.length > 0 && (
                            <div
                                style={{
                                    "--primary_color": colorObj?.primary_color,
                                    "--accent_color4": colorObj?.accent_color4,
                                }}
                            >
                                <Paginator
                                    first={pageParam.page * pageParam.page_size}
                                    rows={pageParam.page_size}
                                    totalRecords={totalPageCount}
                                    onPageChange={onPageChange}
                                    pageLinkSize={isMobile ? 1 : 5}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="filters-modal">
                    <Dialog
                        visible={visible}
                        onHide={() => {
                            if (!visible) return;
                            setVisible(false);
                        }}
                        maximized
                    >
                        <div className="filters-modal-header flex">
                            <div className="title" style={{ color: colorObj?.accent_color1 }}>
                                {t("btnText.filterBy")}
                            </div>
                            <div
                                className="clear-all-txt"
                                onClick={clearAllFilter}
                                style={{
                                    color: colorObj?.accent_color2,
                                    borderBottom: `1px solid ${colorObj?.accent_color2}`,
                                }}
                            >
                                {t("btnText.clearAll")}
                            </div>
                        </div>

                        {Object.keys(dynamicFilters).map(
                            (filterKey) =>
                                dynamicFilters[filterKey].Items &&
                                dynamicFilters[filterKey].Items.length > 0 && (
                                    <div className="tag-filters-main" key={filterKey}>
                                        <MultiSelect
                                            placeholder={dynamicFilters[filterKey].Label}
                                            value={selectedFilters[filterKey]}
                                            onChange={(e) => {
                                                onChangeSelectFilter(e.value, filterKey);
                                            }}
                                            options={dynamicFilters[filterKey].Items || []}
                                            optionLabel="itemName"
                                            maxSelectedLabels={
                                                dynamicFilters[filterKey].Items
                                                    ? dynamicFilters[filterKey].Items.length
                                                    : 3
                                            }
                                            filter
                                            style={{
                                                "--accent_color1": colorObj?.accent_color1,
                                            }}
                                        />
                                    </div>
                                ),
                        )}

                        <div className="col-12">
                            <div className="filter-apply-btn">
                                <Button className="button" onClick={applyMobileFilters}>
                                    {t("btnText.apply")}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </div>
            </section>
        </>
    );
}

export default GallerySection2;
